import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@garnish/overlays';
import { useClient } from 'urql';
import { fromPromise } from 'xstate';

import { type Order } from '@sg/graphql-schema';
import { getLineItemModifications } from '@sg/shared-fe-features-ordering';

import {
  type PartialLineItem,
  type UpdateLineItemParams,
  type UpdateLineItemResponse,
} from '@order/features-ordering';

import { useCart } from '../../../../hooks/useCart/useCart';
import { useGroupOrderTelemetry } from '../../../../hooks/useGroupOrderTelemetry';
import {
  RemoveLineItemDocument,
  type RemoveLineItemMutation,
  type RemoveLineItemMutationVariables,
  UpdateLineItemDocument,
  type UpdateLineItemMutation,
  type UpdateLineItemMutationVariables,
} from '../../GraphQL/BagCart.generated';
import { prepareCart } from '../../utils/prepareCart';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook for updating the line item in the cart.
 * If a {quantity} of 0 is passed, it will call the remove mutation.
 * Otherwise it will call the update mutation for either quantity or custom name.
 */
export const useUpdateLineItem = () => {
  const client = useClient();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();
  const { formatMessage } = useIntl();
  const { isGroupOrder } = useCart();
  const { trackItemActionFailure } = useGroupOrderTelemetry();

  // ─── Mutation ─────────────────────────────────────────────────────────────

  const updateLineItemMutation = useCallback(
    async (lineItem: PartialLineItem, params: UpdateLineItemParams) => {
      const updateMutation = client.mutation<
        UpdateLineItemMutation,
        UpdateLineItemMutationVariables
      >;

      const removeMutation = client.mutation<
        RemoveLineItemMutation,
        RemoveLineItemMutationVariables
      >;

      if (params.quantity === 0) {
        const response = await removeMutation(
          RemoveLineItemDocument,
          {
            input: {
              lineItemId: params.lineItemId,
            },
          },
          {
            requestPolicy: 'network-only',
          },
        ).toPromise();

        return response.data?.removeFromCart;
      }

      const legacyModifications = {
        additions: lineItem.addedIngredients?.map(getIngredientId) ?? [],
        removals: lineItem.removedIngredients?.map(getIngredientId) ?? [],
        substitutions: [],
      };
      const updatedModifications = getLineItemModifications({
        selectedModifiers: lineItem.selectedModifiers ?? [],
        isCustomProduct: lineItem.product?.isCustom ?? false,
      });
      const shouldUseUpdatedModifications = Boolean(lineItem.selectedModifiers);

      const modifications = shouldUseUpdatedModifications
        ? updatedModifications
        : legacyModifications;

      const response = await updateMutation(
        UpdateLineItemDocument,
        {
          input: {
            lineItemId: params.lineItemId,
            productId: params.productId,
            quantity: params.quantity,
            customName: lineItem.customName,
            mixedDressingDetails: lineItem.mixedDressingDetails ?? [],
            ...modifications,
          },
        },
        {
          requestPolicy: 'network-only',
        },
      ).toPromise();

      return response.data?.editLineItemInCart;
    },
    [client.mutation],
  );
  // ─── Helpers ──────────────────────────────────────────────────────────────

  const updateLineItem = useMemo(
    () =>
      fromPromise<UpdateLineItemResponse, UpdateLineItemParams>(async ({ input }) => {
        const { lineItemId, lineItems } = input;
        const lineItem = lineItems.find(({ id }) => id === lineItemId);

        if (!lineItem) return;

        const defaultError = formatMessage(messages.defaultError);

        const response = await updateLineItemMutation(lineItem, input);
        const isBlockedByGroupOrder = isGroupOrder && response?.__typename === 'OrderLocked';

        if (
          response?.__typename === 'EditLineItemInCartSuccess' ||
          response?.__typename === 'RemoveFromCartSuccess'
        ) {
          return prepareCart(response.cart as Partial<Order>);
        }

        if (!response) {
          addNoticeBanner({ text: defaultError, palette: 'caution' });
          input.resetQuantity?.();
          return;
        }

        if (response.__typename === 'ValidationError') {
          addNoticeBanner({
            text: response?.message ?? response?.fieldErrors?.[0].message,
            palette: 'caution',
          });
          input.resetQuantity?.();
          return;
        }

        if (isBlockedByGroupOrder) {
          trackItemActionFailure();
        }

        if (response.__typename === 'OrderLocked') {
          addNoticeBanner({ text: formatMessage(messages.orderLocked), palette: 'neutral' });
          input.resetQuantity?.();

          return;
        }

        if (response.__typename === 'MaxEntreeReached') {
          addNoticeBanner({ text: formatMessage(messages.maxEntreeReached), palette: 'caution' });
          input.resetQuantity?.();

          return;
        }

        if ('message' in response) {
          addNoticeBanner({ text: response.message, palette: 'caution' });
          input.resetQuantity?.();
          return;
        }

        addNoticeBanner({ text: defaultError, palette: 'caution' });
        input.resetQuantity?.();
      }),
    [addNoticeBanner, formatMessage, isGroupOrder, trackItemActionFailure, updateLineItemMutation],
  );

  // ──────────────────────────────────────────────────────────────────────────

  return { updateLineItem };
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const getIngredientId = (ingredient?: { id?: string }) => {
  return { ingredientId: ingredient?.id ?? '' };
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  defaultError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Bag | Update Line Item | Default Error',
  },
  orderLocked: {
    defaultMessage: 'Cannot add item because group order is locked while owner is at checkout.',
    description: 'Bag | Update Line Item | Group order locked',
  },
  maxEntreeReached: {
    defaultMessage:
      'You’ve added the maximum number of entrees for this order. You can still add sides, drinks, or desserts.',
    description: 'Bag | Update Line Item | Group order entree limit reached',
  },
});
